import {DestroyRef, Injectable, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import find from 'lodash/find';
import {
  select,
  Store,
} from '@ngrx/store';
import {lastValueFrom} from 'rxjs';
import {
  convertLobsIdsToIds,
  findLobByLobId,
} from '../../../../common/constants/lobs';
import {Constants} from '../../../../common/constants/constants';
import {AppTranslateService} from '../../../infrastructure/page/i18n/translate/app-translate/app-translate.service';
import {ApiLobService} from '../../../infrastructure/api/common-feature/api-lob/api-lob.service';
import {ILobItem} from '../../../../models/lob.model';
import {selectLobs} from '../../../../store/selector/lob/lob.selectors';
import {ErrorService} from '../../../infrastructure/page/error/error/error.service';
import {LobState} from '../../../../store/reducer/lob/lob.reducer';
import {LobActionsType} from '../../../../store/action/lob/lob.actions';
import {LobId} from '../../../../common/enums/lobId.enum';

@Injectable({
  providedIn: 'root',
})
export class LobService {

  private apiLobService: ApiLobService = inject(ApiLobService);

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private appTranslateService: AppTranslateService = inject(AppTranslateService);

  private errorService: ErrorService = inject(ErrorService);

  private store = inject<Store<LobState>>(Store);

  public get lobList(): ILobItem[] {
    return this.lobsData;
  }

  public lobsData: ILobItem[] = [];

  constructor() {
    this.initStoreData();
  }

  /**
   * Method: Initialization lobs data from Store
   */
  private initStoreData(): void {
    this.store.pipe(select(selectLobs), takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      this.lobsData = data || [];
    });
  }

  /**
   * Method: Gets all Line of Business (LOB) data and dispatches it to the store
   */
  public async getAllLobs(): Promise<void> {
    if (!this.lobsData.length) {
      try {
        const result = await lastValueFrom(this.apiLobService.getAllLobsObservable());
        this.errorService.showResponseErrors(result);
        this.lobsData = result?.data?.commonLobs || [];
        this.store.dispatch({
          type: LobActionsType.load,
          payload: {
            lobs: this.lobsData,
          },
        });
      } catch (err) {
        this.errorService.showGraphQLErrors(err);
      }
    }
  }

  /**
   * Method: Update list lobs
   */
  public async updateListAllLobs(): Promise<void> {
    if (!this.lobsData.length) {
      await this.getAllLobs();
    }
  }

  /**
   * Method: get lob object by id
   * @param id
   */
  public getLobById(id: string | number): ILobItem | undefined | null {
    return id ? find(this.lobsData, ['id', id.toString()]) : null;
  }

  /**
   * Method: get lob object by lobId
   * @param lobId
   */
  public getLobByLobId(lobId: LobId): ILobItem | null {
    return this.getLobById(findLobByLobId(lobId)?.id.toString()) || null;
  }

  /**
   * Method: Translate a list of LOB label keys, sort them alphabetically and join them
   * with a comma. The shared kernel behind every "lobs title" rendering — the id-based
   * helpers below feed it, and object-based callers (whose items already carry a label)
   * can call it directly with `items.map(i => i.label)`.
   * @param labels - LOB label translation keys (missing/null entries tolerated)
   */
  public getLobsTitleByLabels(labels: Array<string | null | undefined>): string {
    if (!labels?.length) {
      return '';
    }
    return labels
      .map(label => this.appTranslateService.getTranslation(label))
      .sort((a, b) => a.localeCompare(b))
      .join(Constants.COMMA_SPACE);
  }

  /**
   * Method: Build the comma-separated, sorted LOB title for a list of `LobId` enums
   * (e.g. a template's `lobs`). Resolves each enum to its label via the LOB list.
   * @param lobs - LobId enums
   */
  public getLobsTitle(lobs: LobId[]): string {
    if (!lobs?.length) {
      return '';
    }
    return this.getLobsTitleByLabels(
      convertLobsIdsToIds(lobs).map(id => this.getLobById(Number(id))?.label),
    );
  }

  /**
   * Method: Build the comma-separated, sorted LOB title for a list of numeric LOB id
   * strings (e.g. `['1', '2']`). Resolves each id to its label via the LOB list.
   * @param ids - numeric LOB id strings
   */
  public getLobsTitleByIds(ids: string[]): string {
    if (!ids?.length) {
      return '';
    }
    return this.getLobsTitleByLabels(
      ids.map(id => this.getLobById(Number(id))?.label),
    );
  }

  /** Method: Check lob is sng by lobId
   * @param lobId
   */
  public isSngLobByLobId(lobId: LobId): boolean {
    const id = findLobByLobId(lobId)?.id.toString() || null;
    return this.isSngLob(id);
  }

  /**
   * Method: Check lob is sng
   * @param id
   */
  public isSngLob(id: string | number): boolean {
    return this.getLobById(id)?.sng;
  }

  /**
   * Method: Check lob in selected options of lob
   * @param lob
   * @param selectedOption
   */
  public hasLobInSelectedOptions(lob: string, selectedOption: ILobItem[]): boolean {
    return selectedOption.some(option => option.id === this.getLobById(lob)?.id);
  }

  /**
   * Method: Logic for select lobs
   * @param lob
   * @param selectedOption
   * @param isOneSelect
   * @param multiple
   */
  public getSelectedLob(lob: string, selectedOption: ILobItem[], isOneSelect: boolean, multiple: boolean): ILobItem[] {
    const lobData = this.getLobById(lob);
    const hasLob = this.hasLobInSelectedOptions(lob, selectedOption);
    let options: ILobItem[] = selectedOption;
    if (isOneSelect) {
      options = hasLob ? selectedOption : [lobData];
      return options;
    }
    if (hasLob) {
      options = selectedOption.filter(option => option.id !== lobData.id);
    } else {
      if (!multiple) {
        options = [];
      }
      options.push(lobData);
    }
    return options;
  }

}
